import React, { useCallback, useEffect, useState } from 'react';
import { getStartListDetails } from 'api/eventStartListApi';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { allEventOptions } from 'config/admin/meet';
import styles from './StartListDetails.module.css';
import Loading from 'components/common/Loading';
import { convertMillisecondsToRecord } from 'utils/time';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import PageHeader from 'components/NewDesign/PageHeader/PageHeader';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupCell, formatGroupRange } from 'utils/groupLabel';

const getRoundType = (roundType) => {
  switch (roundType.toLowerCase()) {
    case 'heat':
      return 'heat';
    case 'semifinal':
      return 'semifinal';
    case 'final':
      return 'final';
    default:
      return 'heat';
  }
};

export const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'TBD';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const StartListDetails = () => {
  const { clubId, meetId, eventTypeCode, ageGroup, gender } = useParams();
  // The athlete's column reads Age and "11", or Year and "Y6" for a club that groups by year level. A group
  // with no name in the meet program is titled by its stored range, shown the same way.
  const { groupingBasis, words } = useGroupLabels();
  const [startListDetails, setStartListDetails] = useState(null);
  const [fetchingStartListDetails, setFetchingStartListDetails] = useState(true);
  const [isMultilaneEvent, setIsMultilaneEvent] = useState(true); // NEW STATE
  const navigate = useNavigate();

  const fetchStartListDetails = useCallback(async () => {
    try {
      setFetchingStartListDetails(true);
      const response = await getStartListDetails(
        meetId,
        eventTypeCode,
        ageGroup,
        gender
      );
      const data = response?.data;
      setStartListDetails(data);
      
      // NEW: Extract isMultilaneEvent from API response
      if (data) {
        setIsMultilaneEvent(data.isMultilaneEvent);
      }
    } catch (err) {
      console.log('[ShowMeet] Fetch start list details error: ', err);
    } finally {
      setFetchingStartListDetails(false);
    }
  }, [meetId, eventTypeCode, ageGroup, gender]);

  useEffect(() => {
    fetchStartListDetails();
  }, [fetchStartListDetails]);

  const eventType = allEventOptions.find((opt) => +opt.value === +eventTypeCode)

  const handleBack = () => {
    navigate(routes.admin.meetStartLists.url(clubId, meetId));
  }

  return (
    <AdminTablePageLayout loading={fetchingStartListDetails && !startListDetails}>
      {/* PageHeader - Gray panel with title and buttons */}
      <PageHeader
        title={`${eventType?.label || ''} ${startListDetails?.groupName || formatGroupRange(groupingBasis, ageGroup)} ${gender} - Start Lists`}
        subTitle={startListDetails?.rounds?.length > 0
          ? `${startListDetails.totalAthletes || 0} registered ${startListDetails.totalAthletes === 1 ? 'athlete' : 'athletes'}`
          : 'No start lists defined for this event'}
      >
        <button 
          onClick={handleBack}  // Changed from inline function
          className={styles.backLink}
        >
          Back to Event List
        </button>
        
      </PageHeader>

      {/* Heats Display */}
      {fetchingStartListDetails ? (
        <div className={styles.loadingContainer}>
          <Loading isLoadingAdminPage={true} />
        </div>
      ) : startListDetails?.rounds?.length > 0 ? (
        startListDetails.rounds.map((round, roundIndex) => (
          <div
            key={round.startListRoundID || roundIndex}
            className={styles.heatCard}
          >
            {/* Heat Header */}
            <div
              className={`${styles.heatHeader} ${
                getRoundType(round.roundType) === 'heat'
                  ? styles.heatHeaderHeat
                  : getRoundType(round.roundType) === 'semifinal'
                    ? styles.heatHeaderSemifinal
                    : styles.heatHeaderFinal
              }`}
            >
              {round.roundType} {round.roundNumber} - {formatDate(round.scheduledStartTime)}
            </div>

            {/* Athletes Table */}
            <table className={styles.athletesTable}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th
                    className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter} ${styles.tableHeaderCellLane}`}
                  >
                    {/* UPDATED: Show "Lane" or "Position" based on event type */}
                    {isMultilaneEvent ? 'Lane' : 'Position'}
                  </th>
                  <th
                    className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter} ${styles.tableHeaderCellBib}`}
                  >
                    Bib
                  </th>
                  <th
                    className={`${styles.tableHeaderCell} ${styles.tableHeaderCellLeft}`}
                  >
                    Athlete
                  </th>
                  <th
                    className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter}`}
                  >
                    {words.value}
                  </th>
                  <th
                   className={`${styles.tableHeaderCell} ${styles.tableHeaderCellLeft} ${styles.tableHeaderCellClub}`}
                  >
                    Club
                  </th>
                  <th
                    className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter} ${styles.tableHeaderCellTime}`}
                  >
                    {getRoundType(round.roundType) === 'heat' ? 'Seed Time' : 'Qualifying Time'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {round.assignments?.map((assignment, index) => (
                  <tr
                    key={assignment.startListAssignmentID || index}
                    className={`${styles.tableRow} ${
                      index < round.assignments.length - 1 ? '' : styles.tableRowLast
                    } ${
                      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
                    }`}
                  >
                    <td
                      className={`${styles.tableCell} ${styles.tableCellCenter} ${styles.tableCellLane}`}
                    >
                      {/* UPDATED: Show laneNumber or positionNumber based on event type */}
                      {isMultilaneEvent ? assignment.laneNumber : assignment.positionNumber}
                    </td>
                    <td
                      className={`${styles.tableCell} ${styles.tableCellCenter} ${styles.tableCellBib}`}
                    >
                      {assignment.athleteNumber}
                    </td>
                    <td className={`${styles.tableCell} ${styles.tableCellAthlete}`}>
                      {assignment.firstName} {assignment.lastName}
                    </td>
                    <td className={`${styles.tableCell} ${styles.tableCellCenter}`}>
                      {formatGroupCell(groupingBasis, assignment.age)}
                    </td>
                    <td className={`${styles.tableCell} ${styles.tableCellClub}`}>
                      {assignment.clubName}
                    </td>
                    <td
                      className={`${styles.tableCell} ${styles.tableCellCenter} ${
                        getRoundType(round.roundType) === 'semifinal'
                          ? styles.tableCellTimeSemifinal
                          : styles.tableCellTime
                      }`}
                    >
                      {assignment.seedValue && assignment.seedValue > 0 
                      ? convertMillisecondsToRecord(assignment.seedValue, 2)
                      : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      ) : null }

      {/* Semifinals Section (Empty State) */}
      <div className={styles.emptySection}>
        <h3 className={styles.emptySectionTitle}>
          Semifinals
        </h3>
        <p className={styles.emptySectionText}>
          Semifinals will be generated after heat results are available
        </p>
      </div>

      {/* Finals Section (Empty State) */}
      <div className={`${styles.emptySection} ${styles.emptySectionSmall}`}>
        <h3 className={styles.emptySectionTitle}>
          Final
        </h3>
        <p className={styles.emptySectionText}>
          Final will be generated after semifinal results are available
        </p>
      </div>
    </AdminTablePageLayout>
  );
};

export default StartListDetails;